export type Project = {
  id: string;
  name: string;
  title?: string;
  owner?: string;
  clusters?: Cluster[];
};

export type Cluster = {
  name: string;
  id: string;
  server: string;
  namespaces?: string[];
  kaapTenant?: {
    name: string;
  };
};

export type DaasProjectConfiguration = {
  project: string;
  cluster?: ClusterConfiguration | null;
};

export type ClusterConfiguration = {
  name: string;
  id: string;
  server: string;
  allowedNamespaces: string[];
  namespace: string;
  kaapTenant?: {
    name: string;
  };
};

export type SourceConfiguration = {
  deploymentType: 'manifests' | 'helm' | 'kustomize' | null;
  repoURL: string;
  targetRevision: string;
  path?: string;
  chart?: string;
  helm?: HelmConfiguration;
  repoType?: RepoType;
};

export type HelmConfiguration = {
  releaseName: string;
  valueFiles: string[];
  valuesObject?: Record<string, unknown>;
  ignoreMissingValueFiles: boolean;
  valuesSource?: ValuesSource;
};

export type ValuesSource = {
  repoURL: string;
  targetRevision: string;
};

export type RepoType = 'git' | 'helm' | 'oci';

export type AutoSyncOptions = {
  prune: boolean;
  selfHeal: boolean;
  allowEmpty: boolean;
};

export type AutoSyncConfig = {
  enabled: boolean;
  options: AutoSyncOptions;
};

export type AutoCreateNamespaceConfig = {
  enabled: boolean;
  managedNamespaceMetadata: KubernetesMetadata;
};

export type SyncOption = {
  key: string;
  value: string;
};

export type SyncPolicy = {
  autoSync: AutoSyncConfig;
  autoCreateNamespace: AutoCreateNamespaceConfig;
  syncOptions: SyncOption[];
};

export type MetadataField = 'annotations' | 'labels';

export type MetadataKeyValuePair = {
  key: string;
  value: string;
};

export type KubernetesMetadata = {
  annotations: MetadataKeyValuePair[];
  labels: MetadataKeyValuePair[];
};

export interface ApplicationBasicInfoValue {
  applicationName: string;
  description: string;
}

export interface ApplicationSetBasicInfoValue {
  applicationSetName: string;
  description: string;
}

export interface DaasApplicationInputUiOptions {
  fillEditFormData?: boolean;
}

export interface DaasApplicationSetInputUiOptions {
  fillEditFormData?: boolean;
}

export interface DaasApplicationSetInputData {
  applicationSetName: string;
  description: string;
  appNameTemplate: string;
  generators: string;
  project: string;
  cluster: ClusterConfiguration | null;
  source: SourceConfiguration | null;
  syncPolicy: SyncPolicy;
  applicationMetadata: KubernetesMetadata;
  imageUpdater: ImageUpdaterConfig;
  templatePatch?: string | null;
  owner?: string;
  /** Catalog entity ref used in edit mode as unique
   *  lookup key (passed via URL) and preserved after selection for catalog refresh. */
  applicationSetRef?: string;
  /** Internal flag: set once after the first auto-prefill from entity data.
   *  Preserved by Backstage formData across navigation to prevent re-prefilling. */
  _initialized?: boolean;
}

export interface DaasApplicationInputData {
  applicationName: string;
  description: string;
  project: string;
  cluster: ClusterConfiguration | null;
  source: SourceConfiguration | null;
  syncPolicy: SyncPolicy;
  applicationMetadata: KubernetesMetadata;
  imageUpdater?: ImageUpdaterConfig;
  owner?: string;
  /** Catalog entity ref used in edit mode as unique
   *  lookup key (passed via URL) and preserved after selection for catalog refresh. */
  applicationRef?: string;
  /** Internal flag: set once after the first auto-prefill from entity data.
   *  Preserved by Backstage formData across navigation to prevent re-prefilling. */
  _initialized?: boolean;
}

export const DEFAULT_CLUSTER: ClusterConfiguration = {
  name: '',
  id: '',
  server: '',
  allowedNamespaces: [],
  namespace: '',
};

export const DEFAULT_METADATA: KubernetesMetadata = {
  annotations: [],
  labels: [],
};

export const DEFAULT_SOURCE_CONFIGURATION: SourceConfiguration = {
  deploymentType: null,
  repoURL: '',
  targetRevision: '',
};

export const DEFAULT_HELM_CONFIGURATION: HelmConfiguration = {
  releaseName: '',
  valueFiles: [''],
  ignoreMissingValueFiles: false,
};

export const DEFAULT_GENERATORS_DATA = `generators: []`;

export const DEFAULT_TEMPLATE_PATCH_DATA = `templatePatch: |
  `;

export interface GitWriteBackConfig {
  repository: string;
  branch: string;
  target: string;
}

export interface ImageUpdaterConfig {
  enabled: boolean;
  imagesYAML: string;
  gitWriteBackConfig?: GitWriteBackConfig;
}

export const DEFAULT_GIT_WRITE_BACK_CONFIG: GitWriteBackConfig = {
  repository: '',
  branch: '',
  target: '',
};

export const DEFAULT_IMAGE_UPDATER_CONFIG: ImageUpdaterConfig = {
  enabled: false,
  imagesYAML: '',
};

export const APPLICATION_DEFAULT_DATA: DaasApplicationInputData = {
  applicationName: '',
  description: '',
  project: '',
  cluster: null,
  source: null,
  syncPolicy: {
    autoSync: {
      enabled: false,
      options: { prune: false, selfHeal: false, allowEmpty: false },
    },
    autoCreateNamespace: {
      enabled: false,
      managedNamespaceMetadata: { annotations: [], labels: [] },
    },
    syncOptions: [],
  },
  applicationMetadata: { annotations: [], labels: [] },
  imageUpdater: DEFAULT_IMAGE_UPDATER_CONFIG,
};

export const APPLICATION_SET_DEFAULT_DATA: DaasApplicationSetInputData = {
  applicationSetName: '',
  description: '',
  appNameTemplate: '',
  generators: '',
  project: '',
  cluster: null,
  source: null,
  syncPolicy: {
    autoSync: {
      enabled: false,
      options: { prune: false, selfHeal: false, allowEmpty: false },
    },
    autoCreateNamespace: {
      enabled: false,
      managedNamespaceMetadata: { annotations: [], labels: [] },
    },
    syncOptions: [],
  },
  applicationMetadata: { annotations: [], labels: [] },
  imageUpdater: DEFAULT_IMAGE_UPDATER_CONFIG,
  templatePatch: '',
};
